import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiPlus, FiUsers, FiTrash2, FiBarChart2, FiArrowLeft } from 'react-icons/fi';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuthStore } from '../../store/authStore';
import ProjectCard from './ProjectCard.jsx';
import { SkeletonCardGrid } from '../common/Skeleton.jsx';
import './ProjectManagement.css';

function ProjectManagement() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSalesReport, setShowSalesReport] = useState(false);
  const [salesReport, setSalesReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editData, setEditData] = useState({});
  const { user: currentUser } = useAuthStore();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    members: [],
    startDate: '',
    endDate: '',
    projectValue: '',
    clientName: '',
    client: '',
    profileName: '',
    platform: '',
    paidAmount: '',
    remainingAmount: '',
    remarks: '',
    salesPersons: [{ user: '', order: 1 }, { user: '', order: 2 }]
  });

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, usersRes, clientsRes] = await Promise.all([
        api.get('/projects'),
        api.get('/users'),
        api.get('/users/clients')
      ]);
      setProjects(projectsRes.data.projects);
      setUsers(usersRes.data.users);
      setClients(clientsRes.data.clients);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateProject = useCallback(async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post('/projects', {
        ...formData,
        projectValue: parseFloat(formData.projectValue) || 0,
        paidAmount: parseFloat(formData.paidAmount) || 0,
        remainingAmount: parseFloat(formData.remainingAmount) || 0,
        salesPersons: formData.salesPersons.filter(sp => sp.user)
      });
      toast.success('Project created successfully!');
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        members: [],
        startDate: '',
        endDate: '',
        projectValue: '',
        clientName: '',
        client: '',
        profileName: '',
        platform: '',
        paidAmount: '',
        remainingAmount: '',
        remarks: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  }, [formData, fetchData, submitting]);

  const handleMemberToggle = useCallback((userId) => {
    setFormData(prev => ({
      ...prev,
      members: prev.members.includes(userId)
        ? prev.members.filter(id => id !== userId)
        : [...prev.members, userId]
    }));
  }, []);

  const handleProjectClick = useCallback((project) => {
    setSelectedProject(project);
    setShowDetailsModal(true);
  }, []);

  const canEdit = ['ceo', 'co_ceo', 'company_manager', 'sales_head'].includes(currentUser?.role);
  const canDelete = ['ceo', 'co_ceo'].includes(currentUser?.role);

  const handleDeleteProject = useCallback(async (projectId, projectName) => {
    if (!window.confirm(`"${projectName}" project aur uska channel permanently delete ho jaayega. Sure ho?`)) return;
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success('Project aur channel delete ho gaya!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete project');
    }
  }, [fetchData]);

  const handleEditOpen = useCallback((project) => {
    setSelectedProject(project);
    setEditData({
      name: project.name || '',
      description: project.description || '',
      status: project.status || 'active',
      projectValue: project.projectValue ?? '',
      clientName: project.clientName || '',
      client: project.client?._id || project.client || '',
      profileName: project.profileName || '',
      platform: project.platform || '',
      paidAmount: project.paidAmount ?? '',
      remainingAmount: project.remainingAmount ?? '',
      remarks: project.remarks || '',
      remarks: project.remarks || '',
      endDate: project.endDate ? project.endDate.split('T')[0] : '',
      salesPersons: (project.salesPersons?.length ? project.salesPersons : [{ user: '', order: 1 }, { user: '', order: 2 }]).map((sp, i) => ({
        user: sp.user?._id || sp.user || '',
        order: sp.order || i + 1
      }))
    });
    setShowDetailsModal(false);
    setShowEditModal(true);
  }, []);

  const handleEditSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (editSubmitting) return;
    setEditSubmitting(true);
    try {
      await api.put(`/projects/${selectedProject._id}`, {
        ...editData,
        projectValue: parseFloat(editData.projectValue) || 0,
        paidAmount: parseFloat(editData.paidAmount) || 0,
        remainingAmount: parseFloat(editData.remainingAmount) || 0,
        salesPersons: editData.salesPersons.filter(sp => sp.user)
      });
      toast.success('Project updated successfully!');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update project');
    } finally {
      setEditSubmitting(false);
    }
  }, [editData, editSubmitting, selectedProject, fetchData]);

  const salesEmployees = useMemo(() => users.filter(u => u.role === 'sales_employee'), [users]);

  const fetchSalesReport = useCallback(async (month, year) => {
    setReportLoading(true);
    try {
      const res = await api.get(`/projects/sales-report?month=${month}&year=${year}`);
      setSalesReport(res.data);
    } catch {
      toast.error('Failed to fetch sales report');
    } finally {
      setReportLoading(false);
    }
  }, []);

  const handleOpenSalesReport = useCallback(() => {
    setShowSalesReport(true);
    fetchSalesReport(reportMonth, reportYear);
  }, [fetchSalesReport, reportMonth, reportYear]);

  const canViewSalesReport = ['sales_head', 'ceo', 'co_ceo', 'company_manager'].includes(currentUser?.role);

  // Filter employees based on current user's role
  const employees = useMemo(() => {
    if (currentUser?.role === 'sales_head') {
      // Sales Head can assign both sales and production employees
      return users.filter(u => ['sales_employee', 'production_employee'].includes(u.role));
    } else if (currentUser?.role === 'production_head') {
      // Production Head can assign both production and sales employees
      return users.filter(u => ['sales_employee', 'production_employee'].includes(u.role));
    }
    // CEO, CO-CEO, Company Manager can see all employees
    return users.filter(u => ['sales_employee', 'production_employee', 'employee'].includes(u.role));
  }, [users, currentUser?.role]);

  if (loading) {
    return (
      <div className="project-management">
        <div className="projects-header">
          <h1><FiUsers /> Project Management</h1>
        </div>
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  return (
    <div className="project-management">
      <div className="projects-header">
        <h1>
          {showSalesReport ? (
            <><FiBarChart2 /> Sales Report</>
          ) : (
            <><FiUsers /> Project Management</>
          )}
        </h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {showSalesReport ? (
            <button className="btn-secondary" onClick={() => setShowSalesReport(false)}>
              <FiArrowLeft /> Back to Projects
            </button>
          ) : (
            <>
              {canViewSalesReport && (
                <button className="btn-secondary" onClick={handleOpenSalesReport}>
                  <FiBarChart2 /> Sales Report
                </button>
              )}
              {currentUser?.role !== 'production_head' && (
                <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
                  <FiPlus /> Create Project
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {!showSalesReport && (
        <div className="projects-grid">
          {projects.length === 0 ? (
            <div className="no-projects">
              <p>No projects yet. Create your first project!</p>
            </div>
          ) : (
            projects.map(project => (
              <ProjectCard
                key={project._id}
                project={project}
                onClick={handleProjectClick}
              />
            ))
          )}
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Project</h2>
            </div>
            <form onSubmit={handleCreateProject}>
              <div className="form-group">
                <label>Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Enter project name"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Project description"
                  rows="3"
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Project Value ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.projectValue}
                    onChange={(e) => setFormData({ ...formData, projectValue: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Client Name *</label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Link Client Account</label>
                  <select
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  >
                    <option value="">Select client account (optional)</option>
                    {clients.map(c => (
                      <option key={c._id} value={c._id}>
                        {c.firstName} {c.lastName} ({c.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Profile Name *</label>
                  <input
                    type="text"
                    value={formData.profileName}
                    onChange={(e) => setFormData({ ...formData, profileName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Platform *</label>
                  <input
                    type="text"
                    value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    placeholder="e.g. Fiverr, Upwork, Direct"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Paid Amount ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.paidAmount}
                    onChange={(e) => setFormData({ ...formData, paidAmount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Remaining Amount ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.remainingAmount}
                    onChange={(e) => setFormData({ ...formData, remainingAmount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  rows={2}
                  placeholder="Optional remarks"
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>1st Sales Person</label>
                  <select
                    value={formData.salesPersons[0]?.user || ''}
                    onChange={(e) => {
                      const sp = [...formData.salesPersons];
                      sp[0] = { user: e.target.value, order: 1 };
                      setFormData({ ...formData, salesPersons: sp });
                    }}
                  >
                    <option value="">Select (optional)</option>
                    {salesEmployees.map(u => (
                      <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>2nd Sales Person</label>
                  <select
                    value={formData.salesPersons[1]?.user || ''}
                    onChange={(e) => {
                      const sp = [...formData.salesPersons];
                      sp[1] = { user: e.target.value, order: 2 };
                      setFormData({ ...formData, salesPersons: sp });
                    }}
                  >
                    <option value="">Select (optional)</option>
                    {salesEmployees.map(u => (
                      <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Assign Team Members</label>
                <div className="members-list">
                  {employees.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>No employees available</p>
                  ) : (
                    employees.map(user => (
                      <label key={user._id} className="member-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.members.includes(user._id)}
                          onChange={() => handleMemberToggle(user._id)}
                        />
                        <span>{user.firstName} {user.lastName} ({user.department})</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Project Details Modal */}
      {showDetailsModal && selectedProject && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedProject.name}</h2>
              <span className={`project-status ${selectedProject.status}`}>
                {selectedProject.status}
              </span>
            </div>
            
            <div style={{padding: '20px'}}>
              {/* Description */}
              <div style={{marginBottom: '20px'}}>
                <h3 style={{fontSize: '16px', marginBottom: '10px', }}>Description</h3>
                <p style={{color: '#666', lineHeight: '1.6'}}>
                  {selectedProject.description || 'No description provided'}
                </p>
              </div>

              {/* Project Info */}
              <div style={{marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                <div>
                  <h3 style={{fontSize: '16px', marginBottom: '10px',}}>Start Date</h3>
                  <p style={{color: '#666'}}>
                    {selectedProject.startDate ? new Date(selectedProject.startDate).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
                <div>
                  <h3 style={{fontSize: '16px', marginBottom: '10px', }}>End Date</h3>
                  <p style={{color: '#666'}}>
                    {selectedProject.endDate ? new Date(selectedProject.endDate).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
              </div>

              {/* Team Members */}
              <div style={{marginBottom: '20px'}}>
                <h3 style={{fontSize: '16px', marginBottom: '15px', }}>Team Members ({selectedProject.members?.length || 0})</h3>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px'}}>
                  {selectedProject.members?.map(member => (
                    <div 
                      key={member._id} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px',
                        background: '#eae9ff',
                        borderRadius: '8px'
                      }}
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: '#0066cc',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}>
                        {member.firstName?.[0]}{member.lastName?.[0]}
                      </div>
                      <div>
                        <div style={{fontWeight: '500', color: 'white'}}>
                          {member.firstName} {member.lastName}
                        </div>
                        <div style={{fontSize: '12px', color: '#666'}}>
                          {member.username}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              {canDelete && (
                <button
                  className="btn-danger"
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleDeleteProject(selectedProject._id, selectedProject.name);
                  }}
                >
                  <FiTrash2 /> Delete Project
                </button>
              )}
              {canEdit && (
                <button className="btn-primary" onClick={() => handleEditOpen(selectedProject)}>
                  Edit Project
                </button>
              )}
              <button className="btn-secondary" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && selectedProject && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Project</h2>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Project Name *</label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editData.status}
                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="on-hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Project Value ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editData.projectValue}
                    onChange={(e) => setEditData({ ...editData, projectValue: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Client Name *</label>
                  <input
                    type="text"
                    value={editData.clientName}
                    onChange={(e) => setEditData({ ...editData, clientName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Link Client Account</label>
                  <select
                    value={editData.client || ''}
                    onChange={(e) => setEditData({ ...editData, client: e.target.value })}
                  >
                    <option value="">No client account linked</option>
                    {clients.map(c => (
                      <option key={c._id} value={c._id}>
                        {c.firstName} {c.lastName} ({c.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Profile Name *</label>
                  <input
                    type="text"
                    value={editData.profileName}
                    onChange={(e) => setEditData({ ...editData, profileName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Platform *</label>
                  <input
                    type="text"
                    value={editData.platform}
                    onChange={(e) => setEditData({ ...editData, platform: e.target.value })}
                    placeholder="e.g. Fiverr, Upwork, Direct"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Paid Amount ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editData.paidAmount}
                    onChange={(e) => setEditData({ ...editData, paidAmount: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Remaining Amount ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editData.remainingAmount}
                    onChange={(e) => setEditData({ ...editData, remainingAmount: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={editData.endDate}
                    onChange={(e) => setEditData({ ...editData, endDate: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <textarea
                  value={editData.remarks}
                  onChange={(e) => setEditData({ ...editData, remarks: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>1st Sales Person</label>
                  <select
                    value={editData.salesPersons?.[0]?.user || ''}
                    onChange={(e) => {
                      const sp = [...(editData.salesPersons || [{ user: '', order: 1 }, { user: '', order: 2 }])];
                      sp[0] = { user: e.target.value, order: 1 };
                      setEditData({ ...editData, salesPersons: sp });
                    }}
                  >
                    <option value="">Select (optional)</option>
                    {salesEmployees.map(u => (
                      <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>2nd Sales Person</label>
                  <select
                    value={editData.salesPersons?.[1]?.user || ''}
                    onChange={(e) => {
                      const sp = [...(editData.salesPersons || [{ user: '', order: 1 }, { user: '', order: 2 }])];
                      sp[1] = { user: e.target.value, order: 2 };
                      setEditData({ ...editData, salesPersons: sp });
                    }}
                  >
                    <option value="">Select (optional)</option>
                    {salesEmployees.map(u => (
                      <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={editSubmitting}>
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Sales Report View — inline, same container */}
      {showSalesReport && (
        <div>
          {/* Month/Year Filter */}
          <div className="report-filter-bar">
            <label>Filter by:</label>
            <select
              value={reportMonth}
              onChange={(e) => setReportMonth(Number(e.target.value))}
              className="report-filter-select"
            >
              {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
            <select
              value={reportYear}
              onChange={(e) => setReportYear(Number(e.target.value))}
              className="report-filter-select"
            >
              {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button className="btn-primary" onClick={() => fetchSalesReport(reportMonth, reportYear)}>
              Apply
            </button>
          </div>

          {reportLoading ? (
            <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
          ) : salesReport ? (
            <>
              {/* Summary Cards */}
              <div className="report-summary-grid">
                {[
                  { label: 'Total Projects', value: salesReport.summary.totalProjects },
                  { label: 'Total Value', value: `$${salesReport.summary.totalValue.toLocaleString()}` },
                  { label: 'Total Paid', value: `$${salesReport.summary.totalPaid.toLocaleString()}` },
                  { label: 'Total Remaining', value: `$${salesReport.summary.totalRemaining.toLocaleString()}` },
                ].map(card => (
                  <div key={card.label} className="report-summary-card">
                    <div className="report-card-label">{card.label}</div>
                    <div className="report-card-value">{card.value}</div>
                  </div>
                ))}
              </div>

              {/* Per Employee Stats */}
              {salesReport.employeeStats.filter(e => e.totalProjects > 0).length === 0 ? (
                <div className="report-empty">📊 No sales data found for this month.</div>
              ) : (
                salesReport.employeeStats.filter(e => e.totalProjects > 0).map(stat => (
                  <div key={stat.employee._id} className="report-employee-block">
                    <div className="report-employee-header">
                      <h3 className="report-employee-name">
                        {stat.employee.firstName} {stat.employee.lastName}
                        <span className="report-employee-username">@{stat.employee.username}</span>
                      </h3>
                      <div className="report-employee-stats">
                        <span className="stat-projects">{stat.totalProjects} projects</span>
                        <span className="stat-paid">Paid: ${stat.totalPaid.toLocaleString()}</span>
                        <span className="stat-remaining">Remaining: ${stat.totalRemaining.toLocaleString()}</span>
                        <span className="stat-total">Total: ${stat.totalValue.toLocaleString()}</span>
                      </div>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="report-table">
                        <thead>
                          <tr>
                            <th>Project</th>
                            <th>Client</th>
                            <th>Platform</th>
                            <th style={{ textAlign: 'right' }}>Value</th>
                            <th style={{ textAlign: 'right' }}>Paid</th>
                            <th style={{ textAlign: 'right' }}>Remaining</th>
                            <th style={{ textAlign: 'center' }}>Role</th>
                            <th style={{ textAlign: 'center' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stat.projects.map(p => (
                            <tr key={p._id}>
                              <td>{p.name}</td>
                              <td className="muted">{p.clientName}</td>
                              <td className="muted">{p.platform}</td>
                              <td style={{ textAlign: 'right' }}>${p.projectValue?.toLocaleString()}</td>
                              <td style={{ textAlign: 'right' }} className="paid">${p.paidAmount?.toLocaleString()}</td>
                              <td style={{ textAlign: 'right' }} className="remaining">${p.remainingAmount?.toLocaleString()}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span className={`role-badge ${p.order === 1 ? 'first' : 'second'}`}>
                                  {p.order === 1 ? '1st' : '2nd'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span className={`project-status ${p.status}`}>{p.status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ProjectManagement;
